import { useGetBusinessPartnerById } from '@/api/business-partner/business-partner.hooks';
import { useItemFacets } from '@/api/items/items.hooks';
import { ItemFacetsFilters } from '@/api/items/items.types';
import { useCatalogFilterStore } from '@/stores/catalog-filter-store';
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useMemo, useState } from 'react';

/** Single-select facet section (Manufacturer, Category) */
function SingleSelectSection({
  title,
  items,
  selectedValue,
  onSelect,
  getId,
  getLabel,
  getCount,
  isOpen,
  onToggleOpen,
}: {
  title: string;
  items: any[];
  selectedValue?: string | number;
  onSelect: (item: any) => void;
  getId: (item: any) => string | number;
  getLabel: (item: any) => string;
  getCount: (item: any) => number;
  isOpen: boolean;
  onToggleOpen: () => void;
}) {
  const open = isOpen || selectedValue != null;

  if (!items.length) return null;

  return (
    <>
      <ListItemButton
        onClick={onToggleOpen}
        sx={{ py: 0.5, px: 1.5 }}
      >
        <ListItemText
          primary={title}
          slotProps={{
            primary: {
              variant: 'subtitle2',
              sx: {
                fontWeight: 600,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                color: 'text.secondary',
              },
            },
          }}
        />
        {open ? (
          <ExpandLess sx={{ fontSize: 18 }} />
        ) : (
          <ExpandMore sx={{ fontSize: 18 }} />
        )}
      </ListItemButton>
      <Collapse in={open}>
        <List disablePadding dense>
          {items.map((item) => {
            const id = getId(item);
            const isSelected = selectedValue === id;
            return (
              <ListItemButton
                key={String(id)}
                selected={isSelected}
                onClick={() => onSelect(isSelected ? null : item)}
                sx={{
                  py: 0.25,
                  px: 1.5,
                  pl: 2.5,
                  minWidth: 0,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    '& .MuiTypography-root': { color: 'white' },
                  },
                }}
              >
                <ListItemText
                  primary={getLabel(item)}
                  sx={{ minWidth: 0 }}
                  slotProps={{
                    primary: {
                      variant: 'body2',
                      noWrap: true,
                      sx: { fontSize: '0.8rem' },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    color: isSelected ? 'rgba(255,255,255,0.7)' : 'text.disabled',
                    fontSize: '0.7rem',
                    flexShrink: 0,
                  }}
                >
                  {getCount(item)}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
      <Divider />
    </>
  );
}

export function FacetSidebar() {
  const { filters, setManufacturer, setItemGroup, setOnlyInStock } =
    useCatalogFilterStore();
  const { data: businessPartner } = useGetBusinessPartnerById();
  const [openSingleKey, setOpenSingleKey] = useState<string | null>('itemGroup');

  const defaultPriceListId = businessPartner?.defaultPriceListId;
  const facetFilters = useMemo<ItemFacetsFilters>(() => {
    const params: ItemFacetsFilters = {};
    if (defaultPriceListId) {
      params.priceListId = defaultPriceListId;
    }
    if (filters.manufacturerId) params.manufacturerId = filters.manufacturerId;
    if (filters.itemGroupId) params.itemGroupId = filters.itemGroupId;
    return params;
  }, [filters, defaultPriceListId]);

  const { data: facets, isLoading } = useItemFacets(facetFilters);

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} height={32} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(2, 52, 76, 0.12) transparent',
        '&::-webkit-scrollbar': { width: 5 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(2, 52, 76, 0.12)',
          borderRadius: 10,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(2, 52, 76, 0.25)',
        },
      }}
    >
      <List disablePadding>
        <ListItemButton
          onClick={() => setOnlyInStock(!filters.onlyInStock)}
          sx={{ py: 0.25, px: 1.5 }}
        >
          <Checkbox
            edge="start"
            size="small"
            checked={!!filters.onlyInStock}
            tabIndex={-1}
            disableRipple
            sx={{ p: 0.5, mr: 0.5 }}
          />
          <ListItemText
            primary="Nur lagernde Artikel"
            slotProps={{
              primary: {
                variant: 'body2',
                sx: { fontSize: '0.8rem' },
              },
            }}
          />
        </ListItemButton>
        <Divider />

        <SingleSelectSection
          title="Hersteller"
          items={facets?.manufacturerFacets ?? []}
          selectedValue={filters.manufacturerId}
          onSelect={(item) => setManufacturer(item?.id, item?.name)}
          getId={(item) => item.id}
          getLabel={(item) => item.name}
          getCount={(item) => item.count}
          isOpen={openSingleKey === 'manufacturer'}
          onToggleOpen={() => setOpenSingleKey(openSingleKey === 'manufacturer' ? null : 'manufacturer')}
        />

        <SingleSelectSection
          title="Kategorie"
          items={facets?.itemGroupFacets ?? []}
          selectedValue={filters.itemGroupId}
          onSelect={(item) => setItemGroup(item?.id, item?.name)}
          getId={(item) => item.id}
          getLabel={(item) => item.name}
          getCount={(item) => item.count}
          isOpen={openSingleKey === 'itemGroup'}
          onToggleOpen={() => setOpenSingleKey(openSingleKey === 'itemGroup' ? null : 'itemGroup')}
        />
      </List>
    </Box>
  );
}
